import React, { useEffect, useState, useMemo } from 'react'
import {
  Box, Typography, CircularProgress, Avatar, Alert, Tooltip, Chip, Button, Checkbox,
  FormControlLabel,
  ListItemIcon, ListItemText, Divider, IconButton, useTheme, TextField, Menu, MenuItem,
  InputAdornment, FormControl, InputLabel, Select
} from '@mui/material'
import EmailIcon from '@mui/icons-material/Email'
import CakeIcon from '@mui/icons-material/Cake'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import WcIcon from '@mui/icons-material/Wc'
import PhoneIcon from '@mui/icons-material/Phone'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import SettingsIcon from '@mui/icons-material/Settings'
import TuneIcon from '@mui/icons-material/Tune'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import WbSunnyIcon from '@mui/icons-material/WbSunny'
import Brightness3Icon from '@mui/icons-material/Brightness3'
import EditIcon from '@mui/icons-material/Edit'
import { useDispatch, useSelector } from 'react-redux'
import { logout } from '../Auth/authActions'
import { fetchProfile, updateProfile } from './profileActions'
import { CLEAR_PROFILE_ERROR } from './profileActionTypes'
import { fetchPreferences } from './preferenceActions'
import { selectPreferencesData, selectProfile } from './profileSelectors'
import BottomNavBar from '../../components/BottomNavBar'
import useUserWebSocket from '../../hooks/useUserWebSocket'
import AddIcon from '@mui/icons-material/Add'
import LogoutIcon from '@mui/icons-material/Logout'
import CancelIcon from '@mui/icons-material/Cancel'
import SaveIcon from '@mui/icons-material/Save'
import LockResetIcon from '@mui/icons-material/LockReset'
import ChangePasswordDialog from './ChangePasswordDialog'
import ManagePreferencesDialog from '../../components/ManagePreferencesDialog'

const BOTTOM_NAV = 64

// Function to generate a consistent color based on a string (like Gmail avatars)
const stringToColor = (string = '') => {
  let hash = 0
  let i

  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash)
  }

  let color = '#'

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff
    // Generate a slightly muted color (not too dark, not too light)
    const colorValue = Math.max(50, Math.min(200, value % 200))
    color += `00${colorValue.toString(16)}`.slice(-2)
  }

  return color
}

// ─── InfoTile ─────────────────────────────────────────────────────────────────
// A single bento-grid cell used in the read-only profile view.

function InfoTile({ icon, label, value, muted, accessory, span }) {
  return (
    <Box sx={{
      gridColumn: span ? '1 / -1' : 'auto',
      bgcolor: 'background.paper',
      borderRadius: 3,
      p: 1.75,
      border: '1px solid',
      borderColor: 'divider',
      display: 'flex',
      flexDirection: 'column',
      gap: 0.75,
      minWidth: 0,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: '10px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: 'rgba(255,56,92,0.10)', color: 'primary.main', flexShrink: 0,
        }}>
          {icon}
        </Box>
        {accessory}
      </Box>
      <Typography sx={{
        color: 'text.secondary', fontWeight: 700, letterSpacing: '0.05em',
        textTransform: 'uppercase', fontSize: '0.65rem',
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontWeight: 600, fontSize: '0.85rem',
        color: muted ? 'text.secondary' : 'text.primary',
        wordBreak: 'break-word',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}>
        {value}
      </Typography>
    </Box>
  )
}

const Profile = ({ toggleTheme }) => {
  const dispatch = useDispatch()
  const { isAuthenticated } = useSelector(state => state.auth || {})
  // Memoized selectors
  const { loading, profile, error, updating } = useSelector(selectProfile)
  const {
    loading: isLoadingPreferences,
    userPreferences = [],
    error: preferencesError
  } = useSelector(selectPreferencesData)

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false)

  // Map location display names to codes
  const locationMap = useMemo(() => ({
    'Alberta': 'AB',
    'British Columbia': 'BC',
    'Manitoba': 'MB',
    'New Brunswick': 'NB',
    'Newfoundland and Labrador': 'NL',
    'Nova Scotia': 'NS',
    'Ontario': 'ON',
    'Prince Edward Island': 'PE',
    'Quebec': 'QC',
    'Saskatchewan': 'SK',
    'Northwest Territories': 'NT',
    'Nunavut': 'NU',
    'Yukon': 'YT'
  }), [])

  // Map gender display names to their corresponding IDs
  const genderMap = useMemo(() => ({
    'Male': 1,
    'Female': 2,
    'Non-Binary': 3,
    'Prefer not to say': 4
  }), [])

  // State for form data
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    contact_number: '',
    location: '',
    gender: '',
    birthday: null,
    is_indigenous_people: false
  })

  const [openDialog, setOpenDialog] = useState(false)
  const [changePasswordOpen, setChangePasswordOpen] = useState(false)

  // WebSocket connection for real-time updates
  useUserWebSocket(profile?.id, isAuthenticated)

  // Fetch preferences when component mounts
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchPreferences())
    }
  }, [dispatch, isAuthenticated])

  const handleOpenDialog = () => setOpenDialog(true)
  const handleCloseDialog = () => setOpenDialog(false)

  // Load profile data into form when profile changes
  useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        email: profile.email || '',
        contact_number: profile.contact_number || '',
        // Convert location display name to code if needed
        location: locationMap[profile.location] || profile.location || '',
        // Convert gender name to ID
        gender: profile.gender ? genderMap[profile.gender] || '' : '',
        birthday: profile.birthday ? new Date(profile.birthday) : null,
        is_indigenous_people: profile.is_indigenous_people || false
      })
    }
  }, [profile, locationMap, genderMap])

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchProfile())
    }
  }, [dispatch, isAuthenticated])

  const handleLogout = () => {
    dispatch(logout())
  }

  // Settings menu state
  const [anchorEl, setAnchorEl] = React.useState(null)
  const theme = useTheme()
  const open = Boolean(anchorEl)
  const handleSettingsClick = (event) => setAnchorEl(event.currentTarget)
  const handleSettingsClose = () => setAnchorEl(null)
  const handleLogoutAndClose = () => {
    handleSettingsClose()
    handleLogout()
  }

  const handleEditClick = () => {
    setIsEditing(true)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    // Reset form data to current profile data
    setFormData({
      first_name: profile.first_name || '',
      last_name: profile.last_name || '',
      email: profile.email || '',
      contact_number: profile.contact_number || '',
      // Convert location display name to code if needed
      location: locationMap[profile.location] || profile.location || '',
      // Convert gender name to ID
      gender: profile.gender ? genderMap[profile.gender] || '' : '',
      birthday: profile.birthday ? new Date(profile.birthday) : null,
      is_indigenous_people: profile.is_indigenous_people || false
    })
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      // Format the date to YYYY-MM-DD before sending to the backend
      const profileUpdateData = {
        ...formData,
        birthday: formData.birthday ? formData.birthday.toISOString().split('T')[0] : null,
        // Ensure gender is sent as a number
        gender: Number(formData.gender) || null,
        is_indigenous_people: formData.is_indigenous_people
      }

      // Dispatch the updateProfile action
      await dispatch(updateProfile(profileUpdateData))

      // If we get here, the update was successful
      setIsEditing(false)

    } catch (error) {
      // Error is already handled in the Redux action
      console.error('Error updating profile:', error)
    }
  }

  const age = profile?.birthday
    ? Math.floor((new Date() - new Date(profile.birthday)) / (1000 * 60 * 60 * 24 * 365.25))
    : null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {/* Scrollable area */}
      <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: 'background.default' }}>
        {loading && !profile && (
          <Box display="flex" justifyContent="center" alignItems="center" height="40vh">
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert
            severity="error"
            sx={{ m: 2 }}
            onClose={() => dispatch({ type: CLEAR_PROFILE_ERROR })}
          >
            {error}
          </Alert>
        )}

        {profile && (
          <>
            {/* ── Hero banner ──────────────────────────────────────────── */}
            <Box sx={{ position: 'relative' }}>
              <Box sx={{
                // Extra height reserves room for the notch/Dynamic Island on
                // real devices, so the gradient itself (not blank space)
                // extends behind the status bar instead of the buttons
                // below getting pushed up underneath it.
                height: 'calc(128px + env(safe-area-inset-top, 0px))',
                position: 'relative',
                overflow: 'hidden',
                background: (t) => t.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #4a1224 0%, #34123f 45%, #06342f 100%)'
                  : 'linear-gradient(135deg, #FF385C 0%, #FF6B83 45%, #00C9B1 100%)',
              }}>
                {/* soft gradient-mesh glow blobs */}
                <Box sx={{ position: 'absolute', width: 220, height: 220, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.16)', filter: 'blur(40px)', top: -70, left: -50 }} />
                <Box sx={{ position: 'absolute', width: 180, height: 180, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.14)', filter: 'blur(36px)', bottom: -80, right: -30 }} />

                {/* floating glass action buttons */}
                <Box sx={{ position: 'absolute', top: 'calc(10px + env(safe-area-inset-top, 0px))', right: 10, display: 'flex', gap: 1 }}>
                  {!isEditing && (
                    <Tooltip title="Edit Profile">
                      <IconButton
                        onClick={handleEditClick}
                        aria-label="Edit profile"
                        sx={{
                          color: '#fff',
                          bgcolor: 'rgba(255,255,255,0.18)',
                          backdropFilter: 'blur(6px)',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Settings">
                    <IconButton
                      onClick={handleSettingsClick}
                      aria-label="Settings"
                      sx={{
                        color: '#fff',
                        bgcolor: 'rgba(255,255,255,0.18)',
                        backdropFilter: 'blur(6px)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                      }}
                    >
                      <SettingsIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Settings Menu */}
              <Menu
                id="profile-settings-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleSettingsClose}
                MenuListProps={{ 'aria-labelledby': 'profile-settings-button' }}
                PaperProps={{ sx: { borderRadius: 3, minWidth: 200 } }}
              >
                <MenuItem onClick={toggleTheme}>
                  <ListItemIcon>
                    {theme.palette.mode === 'dark' ? <WbSunnyIcon fontSize="small" /> : <Brightness3Icon fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText>{theme.palette.mode === 'dark' ? 'Light Mode' : 'Dark Mode'}</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => { handleSettingsClose(); setChangePasswordOpen(true) }}>
                  <ListItemIcon><LockResetIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Change Password</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogoutAndClose} sx={{ color: 'error.main' }}>
                  <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
                  <ListItemText>Logout</ListItemText>
                </MenuItem>
              </Menu>

              {/* Avatar overlapping the banner + name/username */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 2, mt: -6.5 }}>
                <Box sx={{ position: 'relative' }}>
                  <Box sx={{
                    width: 104, height: 104, borderRadius: '50%', p: '3px',
                    background: 'conic-gradient(from 180deg, #FF385C, #FF6B83, #00C9B1, #FF385C)',
                    boxShadow: '0 10px 28px rgba(0,0,0,0.20)',
                  }}>
                    <Avatar
                      src={profile.profile_picture || undefined}
                      sx={(t) => ({
                        width: '100%',
                        height: '100%',
                        fontSize: '2.1rem',
                        fontWeight: 700,
                        bgcolor: profile.profile_picture
                          ? t.palette.background.default
                          : stringToColor(profile.first_name || profile.username || 'U'),
                        border: '3px solid',
                        borderColor: 'background.default',
                      })}
                    >
                      {profile.first_name?.[0]?.toUpperCase() || profile.username?.[0]?.toUpperCase() || 'U'}
                    </Avatar>
                  </Box>
                  {profile.is_verified && (
                    <Tooltip title="Verified by administrator" enterTouchDelay={0} leaveTouchDelay={3000}>
                      <Box
                        tabIndex={0}
                        sx={{
                          position: 'absolute', bottom: 2, right: 2,
                          bgcolor: 'background.paper', borderRadius: '50%',
                          display: 'flex', boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                          border: '2px solid', borderColor: 'background.default',
                        }}
                      >
                        <CheckCircleRoundedIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                      </Box>
                    </Tooltip>
                  )}
                </Box>

                {!isEditing && (
                  <>
                    <Typography variant="h6" sx={{ fontWeight: 800, mt: 1.25, letterSpacing: '-0.2px' }}>
                      {profile.first_name} {profile.last_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      @{profile.username}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>

            {/* Content */}
            <Box sx={{ px: 2, pt: 2.5, pb: `calc(${BOTTOM_NAV + 24}px + env(safe-area-inset-bottom, 0px))` }}>
              {isEditing ? (
                <Box component="form" onSubmit={handleSubmit}>
                  <Box sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 4,
                    p: 2.5,
                    boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
                    '& .MuiFormControl-root': { mb: 2 },
                  }}>
                    <Typography sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.7rem', mb: 1.5 }}>
                      Basic Info
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <TextField fullWidth label="First Name" name="first_name" value={formData.first_name} onChange={handleInputChange} required />
                      <TextField fullWidth label="Last Name" name="last_name" value={formData.last_name} onChange={handleInputChange} required />
                    </Box>
                    <Box sx={{ mb: 1.5 }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="Birthday"
                          value={formData.birthday}
                          onChange={(newValue) => setFormData(prev => ({ ...prev, birthday: newValue }))}
                          inputFormat="yyyy-MM-dd"
                          mask="____-__-__"
                          renderInput={(params) => (
                            <TextField {...params} fullWidth required onFocus={(e) => e.target.showPicker?.()} />
                          )}
                        />
                      </LocalizationProvider>
                    </Box>
                    <FormControl fullWidth sx={{ mb: 1.5 }}>
                      <InputLabel>Gender</InputLabel>
                      <Select name="gender" value={formData.gender} onChange={handleInputChange} label="Gender">
                        <MenuItem value="1">Male</MenuItem>
                        <MenuItem value="2">Female</MenuItem>
                        <MenuItem value="3">Non-Binary</MenuItem>
                        <MenuItem value="4">Prefer not to say</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControlLabel
                      control={
                        <Checkbox name="is_indigenous_people" checked={formData.is_indigenous_people} onChange={handleInputChange} color="primary" />
                      }
                      label="I identify as Indigenous People"
                    />

                    <Divider sx={{ my: 2 }} />

                    <Typography sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.7rem', mb: 1.5 }}>
                      Contact
                    </Typography>
                    <TextField fullWidth label="Email" name="email" type="email" value={formData.email} onChange={handleInputChange} required sx={{ mb: 1.5 }} />
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="contact_number"
                      value={formData.contact_number}
                      onChange={handleInputChange}
                      InputProps={{ startAdornment: <InputAdornment position="start">+1</InputAdornment> }}
                    />

                    <Divider sx={{ my: 2 }} />

                    <Typography sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.7rem', mb: 1.5 }}>
                      Location
                    </Typography>
                    <FormControl fullWidth>
                      <InputLabel>Location</InputLabel>
                      <Select name="location" value={formData.location} onChange={handleInputChange} label="Location">
                        <MenuItem value="AB">Alberta</MenuItem>
                        <MenuItem value="BC">British Columbia</MenuItem>
                        <MenuItem value="MB">Manitoba</MenuItem>
                        <MenuItem value="NB">New Brunswick</MenuItem>
                        <MenuItem value="NL">Newfoundland and Labrador</MenuItem>
                        <MenuItem value="NS">Nova Scotia</MenuItem>
                        <MenuItem value="ON">Ontario</MenuItem>
                        <MenuItem value="PE">Prince Edward Island</MenuItem>
                        <MenuItem value="QC">Quebec</MenuItem>
                        <MenuItem value="SK">Saskatchewan</MenuItem>
                        <MenuItem value="NT">Northwest Territories</MenuItem>
                        <MenuItem value="NU">Nunavut</MenuItem>
                        <MenuItem value="YT">Yukon</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Sticky save/cancel bar — docks above the bottom nav while scrolling the form */}
                  <Box sx={{
                    position: 'sticky',
                    bottom: `calc(${BOTTOM_NAV}px + env(safe-area-inset-bottom, 0px))`,
                    mt: 2,
                    py: 1.5,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1,
                    bgcolor: 'background.default',
                  }}>
                    <Button
                      variant="outlined"
                      onClick={handleCancelEdit}
                      disabled={updating}
                      startIcon={<CancelIcon />}
                      sx={{ borderRadius: 10 }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={updating}
                      startIcon={updating ? null : <SaveIcon />}
                      sx={{
                        borderRadius: 10,
                        background: 'linear-gradient(135deg, #FF385C 0%, #FF6B83 100%)',
                        boxShadow: '0 4px 14px rgba(255,56,92,0.3)',
                        '&:hover': { background: 'linear-gradient(135deg, #D4003A 0%, #FF385C 100%)' },
                      }}
                    >
                      {updating ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                    </Button>
                  </Box>
                </Box>
              ) : (
                <>
                  {/* Bento info grid */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1.5,
                    mb: 2,
                  }}>
                    <InfoTile icon={<EmailIcon sx={{ fontSize: 18 }} />} label="Email" value={profile.email} />
                    <InfoTile
                      icon={<PhoneIcon sx={{ fontSize: 18 }} />}
                      label="Phone"
                      value={profile.contact_number || 'Not provided'}
                      muted={!profile.contact_number}
                    />
                    <InfoTile
                      icon={<CakeIcon sx={{ fontSize: 18 }} />}
                      label="Birthday"
                      value={profile.birthday ? `${profile.birthday} · ${age} yrs` : 'Not set'}
                      muted={!profile.birthday}
                    />
                    <InfoTile
                      icon={<LocationOnIcon sx={{ fontSize: 18 }} />}
                      label="Location"
                      value={profile.location || 'Not specified'}
                      muted={!profile.location}
                    />
                    <InfoTile
                      span
                      icon={<WcIcon sx={{ fontSize: 18 }} />}
                      label="Gender"
                      value={profile.gender || 'Not specified'}
                      muted={!profile.gender}
                      accessory={profile.is_indigenous_people && (
                        <Chip
                          icon={<CheckCircleRoundedIcon sx={{ fontSize: '14px !important' }} />}
                          label="Indigenous"
                          size="small"
                          sx={{
                            height: 22,
                            bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(76,175,80,0.16)' : 'rgba(76,175,80,0.10)',
                            color: 'success.main',
                            '& .MuiChip-icon': { color: 'success.main' },
                          }}
                        />
                      )}
                    />
                  </Box>

                  {/* Preferences card */}
                  <Box sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}>
                    <Box sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      px: 2, pt: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider',
                    }}>
                      <Typography sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                        House Preferences
                      </Typography>
                      <Button
                        size="small"
                        onClick={handleOpenDialog}
                        startIcon={<TuneIcon sx={{ fontSize: 16 }} />}
                        sx={{ fontSize: '0.75rem', borderRadius: 10, minWidth: 0, px: 1.25 }}
                      >
                        Manage
                      </Button>
                    </Box>
                    <Box sx={{ p: 2 }}>
                      {preferencesError ? (
                        <Alert severity="error">{preferencesError}</Alert>
                      ) : isLoadingPreferences ? (
                        <Box display="flex" justifyContent="center" py={1}>
                          <CircularProgress size={20} />
                        </Box>
                      ) : userPreferences.length === 0 ? (
                        <Box sx={{
                          textAlign: 'center', py: 2.5, px: 1.5,
                          border: '1.5px dashed', borderColor: 'divider', borderRadius: 3,
                        }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', mb: 1.5 }}>
                            You haven't picked any house preferences yet.
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={handleOpenDialog}
                            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                            sx={{ borderRadius: 10 }}
                          >
                            Add Preferences
                          </Button>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                          {userPreferences.map((pref) => (
                            <Chip
                              key={pref.id}
                              label={pref.name}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(255,56,92,0.08)',
                                color: 'primary.main',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                border: '1px solid rgba(255,56,92,0.2)',
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </>
        )}

        <ChangePasswordDialog
          open={changePasswordOpen}
          onClose={() => setChangePasswordOpen(false)}
        />

        <ManagePreferencesDialog open={openDialog} onClose={handleCloseDialog} />
      </Box>

      <Box sx={{ flexShrink: 0 }}>
        <BottomNavBar />
      </Box>
    </Box>
  )
}

export default Profile
